import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Country picker + smart Malaysian-first phone input (client 2026-08-28).
//
// UX contract:
//   • Prefix dropdown (default 🇲🇾 +60) with a small pool of common
//     Southeast Asian country codes. Emoji flags — no CDN dependency.
//   • Number input auto-formats as the user types:
//        1234567890 → 12-345 6789   (10-digit mobile)
//        312345678  → 3-1234 5678   (9-digit fixed line)
//     Punctuation is display-only; the underlying value is raw digits.
//   • Leading zeros are stripped automatically ("0123456789" and
//     "123456789" both resolve to the same submission value).
//   • Component reports up to the parent via onChange with:
//        { rawDigits, e164, dialCode }
//     so ClinicSignup can send `e164` straight to the API.
//
// Value handling: uncontrolled internal state. `defaultDialCode` selects
// the initial prefix; caller doesn't need to manage it.

const COUNTRIES = [
  { code: 'MY', dial: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: 'SG', dial: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: 'ID', dial: '+62', flag: '🇮🇩', name: 'Indonesia' },
  { code: 'TH', dial: '+66', flag: '🇹🇭', name: 'Thailand' },
  { code: 'PH', dial: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: 'VN', dial: '+84', flag: '🇻🇳', name: 'Vietnam' },
  { code: 'BN', dial: '+673', flag: '🇧🇳', name: 'Brunei' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
  { code: 'CN', dial: '+86', flag: '🇨🇳', name: 'China' },
  { code: 'AU', dial: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'US', dial: '+1',  flag: '🇺🇸', name: 'United States' },
];

// Malaysian mobile prefixes start with 1 (010–019 → strip leading 0 → 10-19)
// and get formatted 10-digit as "XX-XXX XXXX". Non-mobile 9-digit numbers
// format as "X-XXXX XXXX" (fixed line: 03, 05, 07, 09 → strip 0 → 3/5/7/9).
//
// Bug fix 2026-09-03 — earlier version used `slice(5, 9)` on the trailing
// segment which visually clipped the 10th digit even though the raw
// state stored all 10. Client saw clinic sign-up capped at 9 digits.
// Now uses `slice(5)` so the tail always renders every remaining digit.
function formatMyDigits(digits) {
  const s = String(digits || '');
  if (!s) return '';
  const mobile = /^1/.test(s);
  if (mobile) {
    if (s.length <= 2) return s;
    if (s.length <= 5) return `${s.slice(0, 2)}-${s.slice(2)}`;
    return `${s.slice(0, 2)}-${s.slice(2, 5)} ${s.slice(5)}`;
  }
  if (s.length <= 1) return s;
  if (s.length <= 5) return `${s.slice(0, 1)}-${s.slice(1)}`;
  return `${s.slice(0, 1)}-${s.slice(1, 5)} ${s.slice(5)}`;
}

// Non-MY numbers get a lighter format: XX XXX XXXX with spaces every 3-4.
function formatGeneric(digits, maxLen = 12) {
  const s = String(digits || '').slice(0, maxLen);
  if (s.length <= 3) return s;
  if (s.length <= 6) return `${s.slice(0, 3)} ${s.slice(3)}`;
  return `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}`;
}

function stripDigitsAndZeros(raw) {
  return String(raw || '').replace(/\D+/g, '').replace(/^0+/, '');
}

function PhoneInput({
  defaultDialCode = '+60',
  onChange,
  helper,
  error,
  label = 'Phone',
  placeholder,
  required,
  autoComplete = 'tel',
}) {
  const [dial, setDial] = useState(defaultDialCode);
  const [rawDigits, setRawDigits] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const current = useMemo(
    () => COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0],
    [dial],
  );

  function emit(digits, dialCode) {
    const e164 = digits ? `${dialCode}${digits}` : '';
    onChange?.({ rawDigits: digits, e164, dialCode });
  }

  function handleDigits(e) {
    const digits = stripDigitsAndZeros(e.target.value);
    const cap = dial === '+60' ? 10 : 12;
    const capped = digits.slice(0, cap);
    setRawDigits(capped);
    emit(capped, dial);
  }

  function pickCountry(c) {
    setDial(c.dial);
    setPickerOpen(false);
    emit(rawDigits, c.dial);
  }

  const displayValue = dial === '+60'
    ? formatMyDigits(rawDigits)
    : formatGeneric(rawDigits);

  const inputPlaceholder = placeholder
    || (dial === '+60' ? '12-345 6789' : '123 456 7890');

  return (
    <div className="field">
      {label && <label>{label}</label>}
      <div
        className="row"
        style={{
          border: `1px solid ${error ? 'var(--danger, #dc2626)' : 'var(--border)'}`,
          borderRadius: 8,
          padding: 0,
          background: 'var(--surface)',
          gap: 0,
          position: 'relative',
        }}
      >
        {/* Prefix / country picker */}
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          aria-label="Change country code"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 10px',
            background: 'transparent',
            border: 0,
            borderRight: '1px solid var(--border)',
            cursor: 'pointer',
            fontSize: 15,
            color: 'var(--ink-800)',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden="true">{current.flag}</span>
          <span style={{ fontWeight: 600 }}>{current.dial}</span>
          <ChevronDown size={14} />
        </button>

        <input
          type="tel"
          inputMode="numeric"
          autoComplete={autoComplete}
          value={displayValue}
          onChange={handleDigits}
          placeholder={inputPlaceholder}
          required={required}
          style={{
            flex: 1,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontSize: 16,
            padding: '8px 12px',
            minWidth: 0,
          }}
        />

        {pickerOpen && (
          <div
            role="listbox"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              zIndex: 10,
              background: 'white',
              border: '1px solid var(--border)',
              borderRadius: 8,
              boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
              maxHeight: 260,
              overflowY: 'auto',
              minWidth: 240,
            }}
          >
            {COUNTRIES.map((c) => (
              <button
                type="button"
                key={c.code}
                onClick={() => pickCountry(c)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '8px 12px',
                  background: c.dial === dial ? 'var(--brand-50, #f0f9ff)' : 'transparent',
                  border: 0,
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: 'var(--ink-800)',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden="true">{c.flag}</span>
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ fontWeight: 600, color: 'var(--ink-500)' }}>{c.dial}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {error
        ? <div className="err" style={{ marginTop: 6, fontSize: 12, color: 'var(--danger, #dc2626)' }}>{error}</div>
        : helper ? <div className="hint muted text-xs" style={{ marginTop: 6 }}>{helper}</div> : null}
    </div>
  );
}

export default PhoneInput;
